export type ShortcutCategory =
    | "navigation"
    | "view"
    | "tools"
    | "measure-mode"
    | "section-mode"
    | "panels"
    | "objects"
    | "edit"
    | "actions";

export type ShortcutKey = {
    code: string;
    ctrl?: boolean; // Ctrl on Windows, Cmd on Mac
    shift?: boolean;
    alt?: boolean;
};

export type ShortcutFeature = "objectMeasure";

export type ShortcutDef = {
    id: string;
    category: ShortcutCategory;
    defaultKey: ShortcutKey;
    displayOverride?: string; // shown as-is in shortcut column; "Alt" auto-replaced with "Opt" on Mac
    macDisplayOverride?: string;
    /** Hide this row unless the matching live handler/toolbar flag is on. */
    requiresFeature?: ShortcutFeature;
};

/**
 * The Procore shortcut surface: every entry must fire when `isProcoreDesign` is
 * true. Bindings `useGlobalShortcuts` gates behind `isStandalone` (plain `P` for
 * Point to Point, double-tap `H` for home) are deliberately absent, and shared
 * keys are listed with their Procore behavior — `Space` pans via `useSpacebarPan`
 * here, where standalone flies to selection.
 */
export const SHORTCUT_DEFS: ShortcutDef[] = [
    // Navigation — SDK-level (display only, not remappable from React)
    { id: "nav-look-left", category: "navigation", defaultKey: { code: "ArrowLeft" } },
    { id: "nav-look-right", category: "navigation", defaultKey: { code: "ArrowRight" } },
    { id: "nav-look-up", category: "navigation", defaultKey: { code: "ArrowUp" } },
    { id: "nav-look-down", category: "navigation", defaultKey: { code: "ArrowDown" } },
    { id: "nav-move-forward", category: "navigation", defaultKey: { code: "KeyW" } },
    { id: "nav-move-left", category: "navigation", defaultKey: { code: "KeyA" } },
    { id: "nav-move-backward", category: "navigation", defaultKey: { code: "KeyS" } },
    { id: "nav-move-right", category: "navigation", defaultKey: { code: "KeyD" } },
    { id: "nav-move-up", category: "navigation", defaultKey: { code: "KeyE" } },
    { id: "nav-move-down", category: "navigation", defaultKey: { code: "KeyQ" } },
    {
        id: "nav-speed",
        category: "navigation",
        defaultKey: { code: "ShiftLeft" },
        displayOverride: "Shift (hold)",
    },
    // Navigation — app-layer
    { id: "nav-default", category: "navigation", defaultKey: { code: "KeyV" } },
    { id: "nav-fly", category: "navigation", defaultKey: { code: "KeyF" } },
    { id: "nav-orbit", category: "navigation", defaultKey: { code: "KeyO" } },
    // Tools — activation shortcuts only
    { id: "tool-measure", category: "tools", defaultKey: { code: "KeyM" } },
    { id: "tool-section-activate", category: "tools", defaultKey: { code: "KeyX" } },
    // Measuring tool — in-mode shortcuts
    { id: "tool-point-to-point", category: "measure-mode", defaultKey: { code: "KeyM" } },
    { id: "tool-laser", category: "measure-mode", defaultKey: { code: "KeyL" } },
    { id: "tool-coords", category: "measure-mode", defaultKey: { code: "KeyC" } },
    {
        id: "tool-shortest-distance",
        category: "measure-mode",
        defaultKey: { code: "KeyD" },
        requiresFeature: "objectMeasure",
    },
    // Sectioning tool — activation + in-mode shortcuts
    { id: "tool-section", category: "section-mode", defaultKey: { code: "KeyX" } },
    // View
    { id: "view-reset", category: "view", defaultKey: { code: "KeyR", ctrl: true } },
    { id: "view-xray", category: "view", defaultKey: { code: "KeyX", ctrl: true } },
    { id: "view-home", category: "view", defaultKey: { code: "Digit0", ctrl: true } },
    { id: "view-home-key", category: "view", defaultKey: { code: "Home" } },
    { id: "view-navigation-overlays", category: "view", defaultKey: { code: "Backslash", ctrl: true } },
    {
        id: "view-space-action",
        category: "view",
        defaultKey: { code: "Space" },
        displayOverride: "Space + Drag",
    },
    // Panels
    { id: "panel-object-tree", category: "panels", defaultKey: { code: "KeyO", alt: true } },
    { id: "panel-properties", category: "panels", defaultKey: { code: "KeyP", alt: true } },
    { id: "panel-settings", category: "panels", defaultKey: { code: "Backslash", alt: true } },
    // Objects
    {
        id: "obj-box-select",
        category: "objects",
        defaultKey: { code: "" },
        displayOverride: "Alt + Drag",
    },
    { id: "obj-hide", category: "objects", defaultKey: { code: "KeyH", ctrl: true } },
    { id: "obj-show-all", category: "objects", defaultKey: { code: "KeyH", shift: true } },
    { id: "obj-unhide-last", category: "objects", defaultKey: { code: "KeyU", ctrl: true } },
    // Edit
    { id: "edit-undo", category: "edit", defaultKey: { code: "KeyZ", ctrl: true } },
    {
        id: "edit-redo",
        category: "edit",
        defaultKey: { code: "KeyY", ctrl: true },
        macDisplayOverride: "Cmd+Shift+Z / Cmd+Y",
    },
    // Actions
    { id: "action-confirm", category: "actions", defaultKey: { code: "Enter" } },
    { id: "action-cancel", category: "actions", defaultKey: { code: "Escape" } },
];

const CODE_TO_DISPLAY: Record<string, string> = {
    KeyA: "A",
    KeyB: "B",
    KeyC: "C",
    KeyD: "D",
    KeyE: "E",
    KeyF: "F",
    KeyG: "G",
    KeyH: "H",
    KeyI: "I",
    KeyJ: "J",
    KeyK: "K",
    KeyL: "L",
    KeyM: "M",
    KeyN: "N",
    KeyO: "O",
    KeyP: "P",
    KeyQ: "Q",
    KeyR: "R",
    KeyS: "S",
    KeyT: "T",
    KeyU: "U",
    KeyV: "V",
    KeyW: "W",
    KeyX: "X",
    KeyY: "Y",
    KeyZ: "Z",
    Digit0: "0",
    Digit1: "1",
    Digit2: "2",
    Digit3: "3",
    Digit4: "4",
    Digit5: "5",
    Digit6: "6",
    Digit7: "7",
    Digit8: "8",
    Digit9: "9",
    Escape: "Esc",
    Enter: "Enter",
    NumpadEnter: "Enter",
    ShiftLeft: "Shift",
    ShiftRight: "Shift",
    Home: "Home",
    End: "End",
    Space: "Space",
    Backslash: "\\",
    Slash: "/",
    Comma: ",",
    Period: ".",
    BracketLeft: "[",
    BracketRight: "]",
    Semicolon: ";",
    Quote: "'",
    Minus: "-",
    Equal: "=",
    Backquote: "`",
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowUp: "↑",
    ArrowDown: "↓",
    F1: "F1",
    F2: "F2",
    F3: "F3",
    F4: "F4",
    F5: "F5",
    F6: "F6",
    F7: "F7",
    F8: "F8",
    F9: "F9",
    F10: "F10",
};

export function formatShortcutKey(key: ShortcutKey, isMac: boolean): string {
    const parts: string[] = [];
    if (key.ctrl) {
        parts.push(isMac ? "Cmd" : "Ctrl");
    }
    if (key.shift) {
        parts.push("Shift");
    }
    if (key.alt) {
        parts.push(isMac ? "Opt" : "Alt");
    }
    parts.push(CODE_TO_DISPLAY[key.code] ?? key.code);
    return parts.join("+");
}

export function visibleShortcutDefs(defs: ShortcutDef[], features: Record<ShortcutFeature, boolean>): ShortcutDef[] {
    return defs.filter((def) => !def.requiresFeature || features[def.requiresFeature]);
}

export function shortcutMatchesQuery(
    shortcutLabel: string,
    categoryLabel: string,
    shortcutDisplay: string,
    query: string,
): boolean {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) {
        return true;
    }

    const labelMatches = shortcutLabel.toLocaleLowerCase().includes(normalizedQuery);
    const categoryMatches = normalizedQuery.length >= 3 && categoryLabel.toLocaleLowerCase().includes(normalizedQuery);
    const shortcutMatches = shortcutDisplay.toLocaleLowerCase().includes(normalizedQuery);
    return labelMatches || categoryMatches || shortcutMatches;
}

export const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
    navigation: "settingsShortcuts.categories.navigation",
    tools: "settingsShortcuts.categories.tools",
    "measure-mode": "settingsShortcuts.categories.measureMode",
    "section-mode": "settingsShortcuts.categories.sectionMode",
    view: "settingsShortcuts.categories.view",
    panels: "settingsShortcuts.categories.panels",
    objects: "settingsShortcuts.categories.objects",
    edit: "settingsShortcuts.categories.edit",
    actions: "settingsShortcuts.categories.actions",
};
